import type { ProjectDetail } from '@ai-meeting/shared'
import { CalendarIcon, ClockIcon, ExternalLinkIcon } from '@/components/Icons'
import { fmt } from '@/constants/ui-locale'
import { useUiStrings } from '@/hooks/useUiStrings'
import { formatDateTime } from '@/utils/formatDateTime'
import { useStore } from '@/store/useStore'

interface ProjectOverviewTabProps {
  project: ProjectDetail
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 border-b border-[var(--border-color)] px-5 py-4 last:border-b-0 sm:flex-row sm:gap-6 sm:px-6 sm:py-5">
      <dt className="w-full shrink-0 text-xs font-medium text-[var(--text-secondary)] sm:w-48">{label}</dt>
      <dd className="min-w-0 flex-1 text-sm text-[var(--text-primary)]">{children}</dd>
    </div>
  )
}

/**
 * The project's own facts: what it is called, its key, and what this product
 * has recorded against it.
 *
 * No client and no description row. Neither exists — Backlog's project object
 * carries three usable fields and feature flags, and the local `Project` table
 * has no column for either — so a row for them would be a promise with nothing
 * behind it.
 */
export default function ProjectOverviewTab({ project }: ProjectOverviewTabProps) {
  const { t } = useUiStrings()
  const uiLocale = useStore((s) => s.uiLocale)

  const total =
    project.meetingCount === 1
      ? t.projectDetailTotalMeetingsOne
      : fmt(t.projectDetailTotalMeetings, { count: project.meetingCount })

  return (
    <dl className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-[var(--shadow-sm)]">
      <Row label={t.projectDetailName}>
        <span className="font-bold">{project.name}</span>
      </Row>
      <Row label={t.projectDetailKey}>
        <span className="font-semibold">{project.projectKey}</span>
      </Row>
      <Row label={t.projectDetailMetrics}>
        <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
          <span className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" />
            <span className="tabular-nums">{total}</span>
          </span>
          <span className="flex items-center gap-2">
            <ClockIcon className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" />
            {/* A fixed `dd/MM/yyyy, HH:mm` rather than "Yesterday": the date
                module fixes the format on purpose, and a relative one would
                have to pick a timezone to be relative to. */}
            <span className="tabular-nums">
              {project.lastMeetingAt === null
                ? t.projectDetailNoMeetings
                : fmt(t.projectDetailLastMeeting, {
                    when: formatDateTime(project.lastMeetingAt, uiLocale),
                  })}
            </span>
          </span>
        </div>
      </Row>
      <Row label="Backlog">
        <a
          href={project.link}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 font-semibold text-[var(--accent)] underline underline-offset-2"
        >
          {t.projectDetailOpenInBacklog}
          <ExternalLinkIcon className="h-3.5 w-3.5" />
        </a>
      </Row>
    </dl>
  )
}
